import { Component, computed, input, Pipe, PipeTransform } from "@angular/core";
import { SharedModule } from "../../shared/shared.module";


@Pipe({
  name: 'replaceUnderscore'
})
export class ReplaceUnderscorePipe implements PipeTransform {
  transform(value: string): string {
    return value ? value.replace(/_/g, ' ') : value;
  }
}

/** A job referenced by a parameters_agent table. */
export interface TableJob {
  id: string;
  name: string;
}

/** A single parameter row in a comparison table. */
export interface ComparisonRow {
  name: string;
  /** value keyed by job id */
  values: { [jobId: string]: string };
}

/**
 * One table emitted by the parameters_agent, found in `detail.data[]`.
 * `type` selects how it is rendered:
 *  - "job_parameters": a single job's parameters as a flat name→value map (`columns`).
 *  - "table": a parameter comparison across one or more jobs (`jobs` + `rows`).
 */
export interface AgentTable {
  type: string;
  /** Single object for "job_parameters", array for "table". */
  jobs?: TableJob | TableJob[];
  /** "table" payload: one row per parameter, values keyed by job id. */
  rows?: ComparisonRow[];
  /** "job_parameters" payload: a flat parameter name → value map. */
  columns?: { [param: string]: any };
  [key: string]: any;
}

/** A single Parameter | Value row for the "job_parameters" layout. */
interface ParamRow {
  name: string;
  value: string;
}

/** Renders empty/null parameter values as an em dash. */
function display(value: any): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

@Component({
  selector: "app-xwai-table",
  templateUrl: "./xwai-table.component.html",
  styleUrls: ["./xwai-table.component.less"],
  imports: [SharedModule, ReplaceUnderscorePipe],
})
export class XwaiTableComponent {
  readonly table = input.required<AgentTable>();

  /** Raw input, for the unknown-type fallback. */
  readonly raw = computed(() => this.table());

  readonly type = computed(() => this.table()?.type ?? "unknown");

  /**
   * Rendering layout, decoupled from the raw `type`:
   *  - "single": one job's flat Parameter | Value table.
   *  - "matrix": parameters down the side, one column per job
   *    (emitted as either "table" or "comparison" — rendered identically).
   *  - "unknown": fall back to the raw payload.
   */
  readonly layout = computed<"single" | "matrix" | "unknown">(() => {
    switch (this.type()) {
      case "job_parameters":
        return "single";
      case "table":
      case "comparison":
        return "matrix";
      default:
        return "unknown";
    }
  });

  // --- "job_parameters": one job, a flat Parameter | Value table ---

  /** The single job a "job_parameters" table describes. */
  readonly job = computed<TableJob | null>(() => {
    const j = this.table()?.jobs;
    return Array.isArray(j) ? (j[0] ?? null) : (j ?? null);
  });

  readonly paramRows = computed<ParamRow[]>(() => {
    const cols = this.table()?.columns;
    if (!cols) return [];
    return Object.entries(cols).map(([name, value]) => ({
      name,
      value: display(value),
    }));
  });

  // --- "table": parameters down the side, one column per job ---

  readonly jobs = computed<TableJob[]>(() => {
    const j = this.table()?.jobs;
    return Array.isArray(j) ? j : j ? [j] : [];
  });

  readonly rows = computed<ComparisonRow[]>(() => this.table()?.rows ?? []);

  /** Cell lookup for the comparison table, with an em-dash fallback. */
  cell(row: ComparisonRow, jobId: string): string {
    return display(row.values?.[jobId]);
  }

  /** Horizontal scroll width so the sticky parameter column has room to detach. */
  readonly scrollX = computed(() => `${220 + this.jobs().length * 220}px`);
}
